// ────────────────────────────────────────────────────────────────
// core/http/api.interceptor.ts — the SPA's request contract (§6.1)
//  1. Attaches the bearer access token to every non-auth request.
//  2. Injects `X-Confirm-Env: prd` on exactly one mutating request
//     while EnvStore.prdArmed is set (armed by the typed-confirm dialog).
//  3. On 401, refreshes ONCE then replays the original request; a failed
//     refresh forces sign-out.
// Auth endpoints (/api/auth/*) authenticate themselves and are exempt
// from both the bearer attach and the 401-refresh loop.
// ────────────────────────────────────────────────────────────────

import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { SessionStore } from '../auth/session.store';
import { AuthService } from '../auth/auth.service';
import { EnvStore } from '../env/env.store';

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  const session = inject(SessionStore);
  const auth = inject(AuthService);
  const envStore = inject(EnvStore);

  const isAuthEndpoint = req.url.includes('/api/auth/');
  const token = session.accessToken();

  let headers = req.headers;
  if (token && !isAuthEndpoint) {
    headers = headers.set('Authorization', `Bearer ${token}`);
  }

  // One-shot prd confirmation header (armed by the shared prd dialog).
  if (!isAuthEndpoint && envStore.prdArmed() && MUTATING.has(req.method)) {
    headers = headers.set('X-Confirm-Env', 'prd');
    envStore.disarmPrd();
  }

  const outbound = headers === req.headers ? req : req.clone({ headers });

  return next(outbound).pipe(
    catchError((err) => {
      const refreshable = err?.status === 401 && !isAuthEndpoint && !!token;
      if (!refreshable) return throwError(() => err);

      // Refresh once, then replay the original request with the new token.
      return auth.refresh().pipe(
        switchMap(() => {
          const retried = req.clone({
            setHeaders: { Authorization: `Bearer ${session.accessToken() ?? ''}` },
          });
          return next(retried);
        }),
        catchError(() => {
          auth.forceLogout();
          return throwError(() => err);
        })
      );
    })
  );
};
